import { List } from "immutable";
import { RootState } from "common/reducers";
import {
  EstimateLineItemRecord,
  ValidationResultRecord,
  ValidationResultDetailRecord,
  ValidationResultPhotoRecord,
} from "common/records/EstimateLineItem";
import { ValidationKind } from "common/records/Validation";
import { validationFromItemUuid } from "common/selectors/validation.selectors";
import { ptogsByProductId } from "common/selectors/productToOptionGroupLink.selectors";
import { productsByIdForActivatedPriceList } from "common/selectors/product.selectors";
import { productOptionGroupById } from "common/selectors/productOptionGroup.selectors";
import { image } from "common/selectors/image.selectors";
import { estimateLineItemOption } from "common/selectors/estimateLineItemOption.selectors";

/**
 * Checks whether a product or any ancestor in the product hierarchy has a UUID that
 * appears in the given uuid list. Uses the APL-scoped product map for the walk so that
 * only products belonging to the given activated price list are considered, avoiding
 * cross-APL UUID contamination when multiple APLs have been loaded into the global store.
 */
export const checkLineItemMatchesUuids = (
  state: RootState,
  productId: number,
  uuids: List<string>,
  activatedPriceListId: number,
): boolean => {
  const aplProducts = productsByIdForActivatedPriceList(state, activatedPriceListId);
  const visited = new Set<number>();
  let currentId: number | null | undefined = productId;

  while (currentId != null && !visited.has(currentId)) {
    visited.add(currentId);
    const product = aplProducts.get(currentId);
    if (!product) break;
    if (uuids.includes(product.uuid)) return true;
    currentId = product.parent_id ?? null;
  }

  return false;
};

/**
 * Finds the validation rule for a product, walking the parent_id chain.
 */
export const findValidationRule = (
  state: RootState,
  productId: number,
  activatedPriceListId: number,
  kind: ValidationKind,
) => {
  const aplProducts = productsByIdForActivatedPriceList(state, activatedPriceListId);
  const visited = new Set<number>();
  let currentId: number | null | undefined = productId;

  while (currentId != null && !visited.has(currentId)) {
    visited.add(currentId);
    const product = aplProducts.get(currentId);
    if (!product) break;
    const rule = validationFromItemUuid(state, kind, product.uuid);
    if (rule) return rule;
    currentId = product.parent_id ?? null;
  }

  return undefined;
};

/**
 * Validates product-level rules (required categories, optional categories, photo upload).
 */
export const validateLineItemProduct = (
  state: RootState,
  lineItem: EstimateLineItemRecord,
  estimateKind: ValidationKind,
  activatedPriceListId: number,
  allIncludedLineItems: EstimateLineItemRecord[],
): { validated: boolean; validation_results: ValidationResultRecord[] } => {
  if (!lineItem.product_id) {
    return { validated: true, validation_results: [] };
  }

  const rule = findValidationRule(state, lineItem.product_id, activatedPriceListId, estimateKind);
  if (!rule) {
    return { validated: true, validation_results: [] };
  }

  const { definition, name } = rule;
  const results: ValidationResultRecord[] = [];
  let validated = true;

  // Included line items with quantity > 0 and a product
  const eligibleItems = allIncludedLineItems.filter((li) => li.product_id && (li.quantity ?? 0) > 0 && !li._destroy);

  // Required categories
  if (definition.required_categories.size > 0) {
    const unmetCategories: string[] = [];

    for (const category of definition.required_categories) {
      const met = eligibleItems.some((li) =>
        checkLineItemMatchesUuids(state, li.product_id!, category.product_uuids, activatedPriceListId),
      );
      if (!met) {
        unmetCategories.push(category.name);
      }
    }

    if (unmetCategories.length > 0) {
      validated = false;
      results.push(
        new ValidationResultRecord({
          name,
          required: new ValidationResultDetailRecord({
            qty_met: definition.required_categories.size - unmetCategories.length,
            qty_required: definition.required_categories.size,
            custom_message: definition.required_categories_message,
            categories: List(unmetCategories),
          }),
        }),
      );
    }
  }

  // Optional categories (min_required_qty)
  if (definition.categories.size > 0 && definition.min_required_qty != null) {
    const unmetCategories: string[] = [];
    let metCount = 0;

    for (const category of definition.categories) {
      const met = eligibleItems.some((li) =>
        checkLineItemMatchesUuids(state, li.product_id!, category.product_uuids, activatedPriceListId),
      );
      if (met) {
        metCount++;
      } else {
        unmetCategories.push(category.name);
      }
    }

    if (metCount < definition.min_required_qty) {
      validated = false;
      results.push(
        new ValidationResultRecord({
          name,
          optional: new ValidationResultDetailRecord({
            qty_met: metCount,
            qty_required: definition.min_required_qty,
            categories: List(unmetCategories),
            custom_message: definition.categories_message,
          }),
        }),
      );
    }
  }

  // Photo upload required
  if (definition.photo_upload_required) {
    const hasNonDestroyedImage = lineItem.image_ids.some((imageId) => {
      const img = image(state, { imageId });
      return img && !img._destroy;
    });

    if (!hasNonDestroyedImage) {
      validated = false;
      results.push(
        new ValidationResultRecord({
          name,
          photo_upload_required: new ValidationResultPhotoRecord({
            custom_message: definition.photo_upload_message,
          }),
        }),
      );
    }
  }

  return { validated, validation_results: results };
};

/**
 * Validates option group rules for a line item's product.
 */
export const validateLineItemOptionGroups = (
  state: RootState,
  lineItem: EstimateLineItemRecord,
  estimateKind: ValidationKind,
): { validated: boolean; validation_results: ValidationResultRecord[] } => {
  if (!lineItem.product_id) {
    return { validated: true, validation_results: [] };
  }

  const ptogs = ptogsByProductId(state, lineItem.product_id);
  const results: ValidationResultRecord[] = [];
  let validated = true;

  for (const ptog of ptogs) {
    const optionGroup = productOptionGroupById(state, ptog.option_group_id);
    if (!optionGroup || !optionGroup.uuid) continue;

    const rule = validationFromItemUuid(state, estimateKind, optionGroup.uuid);
    if (!rule || rule.definition.kind !== "product_option_group") continue;

    const { required_selection_count, required_selection_count_message } = rule.definition;
    if (required_selection_count == null) continue;

    // Count options belonging to this group
    let selectedCount = 0;
    for (const optionId of lineItem.option_ids) {
      const option = estimateLineItemOption(state, { estimateLineItemOptionId: optionId });
      if (option && !option._destroy && option.product_option_group_id === ptog.option_group_id) {
        selectedCount++;
      }
    }

    if (selectedCount < required_selection_count) {
      validated = false;
      results.push(
        new ValidationResultRecord({
          name: rule.name,
          requiredSelection: new ValidationResultDetailRecord({
            qty_met: selectedCount,
            qty_required: required_selection_count,
            custom_message: required_selection_count_message,
            categories: List([optionGroup.name]),
          }),
        }),
      );
    }
  }

  return { validated, validation_results: results };
};

/**
 * Top-level validation for a single line item.
 * Combines product-level and option-group-level validation.
 */
export const validateLineItem = (
  state: RootState,
  lineItem: EstimateLineItemRecord,
  estimateKind: ValidationKind,
  activatedPriceListId: number,
  allIncludedLineItems: EstimateLineItemRecord[],
): { validated: boolean; validation_results: ValidationResultRecord[] } => {
  const productResult = validateLineItemProduct(
    state,
    lineItem,
    estimateKind,
    activatedPriceListId,
    allIncludedLineItems,
  );

  // Option-group rules are looked up purely by the option group's uuid + kind (org-global), so —
  // unlike product validation — they don't take the activated price list. RSF-21996.
  const optionResult = validateLineItemOptionGroups(state, lineItem, estimateKind);

  const allResults = [...productResult.validation_results, ...optionResult.validation_results];

  return {
    validated: productResult.validated && optionResult.validated,
    validation_results: allResults,
  };
};
